import {testIdProps} from '@github-ui/test-id-props'
import {ArrowSwitchIcon, KebabHorizontalIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label, Stack} from '@primer/react'
import {NavList} from '@primer/styled-react'
import {useMemo} from 'react'

import type {MemexProjectColumnId} from '../../../api/columns/contracts/memex-column'
import {getColumnIcon} from '../../../components/column-detail-helpers'
import {NavLinkActionListItem} from '../../../components/react-router/action-list-nav-link-item'
import {isColumnUserEditable} from '../../../helpers/is-column-editable'
import {sortColumnsDeterministically} from '../../../helpers/util'
import {useColumnWarning} from '../../../hooks/use-column-warning'
import type {ColumnModel} from '../../../models/column-model'
import {useNavigate} from '../../../router'
import {useProjectRouteParams} from '../../../router/use-project-route-params'
import {PROJECT_SETTINGS_CUSTOM_FIELDS_ROUTE, PROJECT_SETTINGS_FIELD_ROUTE} from '../../../routes'
import {useIssueFields} from '../../../state-providers/issue-fields/use-issue-fields'
import {IssueFieldResources} from '../../../strings'
import styles from './column-list-nav.module.css'

type ColumnListProps = {
  columns: Array<ColumnModel>
  addNewAnchorRef: React.RefObject<HTMLButtonElement> | null
  /** Called when the "new field" action is selected */
  onNewField: () => void
  onFieldNavCallback?: (memexProjectColumnId: MemexProjectColumnId, event: React.MouseEvent) => void
}

export function ColumnListNav({columns: allColumns, addNewAnchorRef, onNewField, onFieldNavCallback}: ColumnListProps) {
  const {issueFieldsEnabled} = useIssueFields()

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const columns = useMemo(
    () => allColumns.filter(column => isColumnUserEditable(column)).sort(sortColumnsDeterministically),
    [allColumns],
  )

  return (
    // Providing the `sx` attribute ensures no divider is rendered
    // See https://github.com/github/primer/issues/5391
    // eslint-disable-next-line @github-ui/github-monorepo/no-sx
    <NavList.Group sx={{}} className={styles.columnListNav} {...testIdProps(`project-column-settings-list`)}>
      <Stack className={styles.headingGroup} direction="horizontal" align="center">
        <Stack.Item grow>
          <NavList.GroupHeading as="h2">
            {issueFieldsEnabled ? <span>Fields</span> : <span>Custom fields</span>}
          </NavList.GroupHeading>
        </Stack.Item>
        <IconButton
          icon={PlusIcon}
          aria-label={issueFieldsEnabled ? 'Add field' : 'New field'}
          variant="invisible"
          ref={addNewAnchorRef}
          onClick={onNewField}
          {...testIdProps('add-new-field-button')}
        />
        <ColumnListOptionsMenu />
      </Stack>
      {useMemo(
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
        () =>
          columns.map(col => (
            <ColumnListItem
              key={col.id}
              column={col}
              onLinkClick={(e: React.MouseEvent) => {
                if (!onFieldNavCallback) return
                onFieldNavCallback(col.id, e)
              }}
            />
          )),
        // eslint-disable-next-line react-hooks/preserve-manual-memoization
        [columns, onFieldNavCallback],
      )}
    </NavList.Group>
  )
}

type ColumnListItemProps = {
  column: ColumnModel
  onLinkClick: (e: React.MouseEvent) => void
}

function ColumnListItem({column, onLinkClick}: ColumnListItemProps) {
  const projectRouteParams = useProjectRouteParams()
  const Icon = getColumnIcon(column.dataType)
  const warningKind = useColumnWarning(column)
  const showColumnWarning = !!warningKind

  const warningMessage = showColumnWarning
    ? `${
        warningKind === 'column-name-collides-with-issue-field'
          ? IssueFieldResources.fieldNameCollisionTooltip
          : 'Action required'
      }`
    : column.name

  return (
    <NavLinkActionListItem
      to={{
        pathname: PROJECT_SETTINGS_FIELD_ROUTE.generatePath({
          ...projectRouteParams,
          fieldId: encodeURIComponent(column.id),
        }),
      }}
      onClick={onLinkClick}
      {...testIdProps(`ColumnSettingsItem{id: ${column.name}}`)}
    >
      <NavList.LeadingVisual
        {...testIdProps(`ColumnSettingsItemIcon{id: ${column.name}}`)}
        className={styles.leadingVisual}
      >
        {/* eslint-disable-next-line react-hooks/static-components */}
        <Icon />
      </NavList.LeadingVisual>
      {column.name}
      {showColumnWarning && (
        <NavList.TrailingVisual {...testIdProps(`ColumnSettingsItemBadge{id: ${column.name}}`)}>
          <Label aria-label={warningMessage} variant="attention">
            Action required
          </Label>
        </NavList.TrailingVisual>
      )}
    </NavLinkActionListItem>
  )
}

function ColumnListOptionsMenu() {
  const {issueFieldsEnabled} = useIssueFields()
  const projectRouteParams = useProjectRouteParams()
  const navigate = useNavigate()
  const onEditClick = () => {
    const url = PROJECT_SETTINGS_CUSTOM_FIELDS_ROUTE.generatePath(projectRouteParams)
    navigate(url)
  }

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          aria-label={issueFieldsEnabled ? 'Reorder fields' : 'Custom field options'}
          variant="invisible"
          {...testIdProps('custom-field-options-button')}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList>
          <ActionList.Item onSelect={onEditClick} {...testIdProps('edit-custom-fields-action')}>
            <ActionList.LeadingVisual>
              <ArrowSwitchIcon />
            </ActionList.LeadingVisual>
            Reorder fields
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ ColumnListNav.displayName ||= 'ColumnListNav' } catch {}
try{ ColumnListItem.displayName ||= 'ColumnListItem' } catch {}
try{ Icon.displayName ||= 'Icon' } catch {}
try{ ColumnListOptionsMenu.displayName ||= 'ColumnListOptionsMenu' } catch {}